import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Eye, Loader2 } from 'lucide-react'
import { ContentStatusBadge } from '../-components/ContentStatusBadge'
import { CourseScaffold } from '../-components/CourseScaffold'
import { LessonPreviewDialog } from '../-components/LessonPreviewDialog'
import type { LearningLesson } from '@/types/learning.types'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useLearningVersion } from '@/hooks/repositories/useLearningRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'

export const Route = createFileRoute('/_app/learning/admin/$versionId/preview')(
  {
    beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.author),
    component: RouteComponent,
  },
)

/**
 * The course as an employee will see it, before it is published (sc-1245).
 *
 * A deliberate mirror of the learner's course page — it renders the same
 * CourseScaffold, so the two cannot drift. Where they differ, they differ
 * because the preview would otherwise be lying: progress is 0 and lessons open
 * a preview rather than the player, since an author reading their own draft
 * must not leave completion records behind.
 *
 * It reads the ADMIN version endpoint, not the learner one. The learner
 * endpoint is addressed by slug and only ever serves the published version —
 * which is exactly the draft-safety property worth keeping, and the reason this
 * page has to exist rather than pointing an author at `/learning/{slug}`.
 */
function RouteComponent() {
  const { versionId } = Route.useParams()
  const { data: version, isPending } = useLearningVersion(versionId)
  const [previewing, setPreviewing] = React.useState<LearningLesson | null>(
    null,
  )

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!version) {
    return (
      <div className="py-4">
        <PageTitle title="Learning — Preview" />
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            This version no longer exists.
          </CardContent>
        </Card>
      </div>
    )
  }

  const totalMinutes =
    version.estimatedMinutes ??
    (version.lessons.some((lesson) => lesson.estimatedMinutes != null)
      ? version.lessons.reduce(
          (sum, lesson) => sum + (lesson.estimatedMinutes ?? 0),
          0,
        )
      : null)

  return (
    <div className="py-4">
      <PageTitle title={`Learning — Preview: ${version.title}`} />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/admin/$versionId" params={{ versionId }}>
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Back to editing
        </Link>
      </Button>

      {/* Say plainly that this is not the real thing. An author who forgets
          they are in a preview will read "0 of 6 complete" as a bug. */}
      <div className="mb-5 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
        <Eye className="size-4 shrink-0 text-primary" aria-hidden />
        <span className="font-medium">Preview</span>
        <span className="text-muted-foreground">
          How this looks to an employee. Nothing here is recorded, and progress
          always shows empty.
        </span>
        <ContentStatusBadge status={version.status} />
      </div>

      <CourseScaffold
        heroImageAssetId={version.heroImageAssetId}
        title={version.title}
        summary={version.summary}
        agendaMarkdown={version.agendaMarkdown}
        introVideoAssetId={version.introVideoAssetId}
        introExternalUrl={version.introExternalUrl}
        lessons={version.lessons}
        chapters={version.chapters}
        attachments={version.attachments}
        totalMinutes={totalMinutes}
        // Always 0: a preview showing the author's own progress would
        // misrepresent what a new starter sees.
        completedCount={0}
        meta={version.categories.map((term) => (
          <span key={term.id}>{term.name}</span>
        ))}
        progressNote={
          <p className="text-xs text-muted-foreground">
            What a new starter sees on day one.
          </p>
        }
        emptyLessonsNote="No lessons yet — an employee would see an empty course."
        primaryAction={
          <Button
            disabled={version.lessons.length === 0}
            onClick={() => setPreviewing(version.lessons[0] ?? null)}
          >
            Start course
          </Button>
        }
        renderLesson={(lesson, _index, content) => (
          <button
            type="button"
            onClick={() =>
              setPreviewing(
                version.lessons.find((row) => row.id === lesson.id) ?? null,
              )
            }
            className="flex w-full items-start gap-3 rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/50"
          >
            {content}
          </button>
        )}
      />

      {previewing && (
        <LessonPreviewDialog
          lesson={previewing}
          open
          onOpenChange={(next) => {
            if (!next) setPreviewing(null)
          }}
        />
      )}
    </div>
  )
}
